/**
 * @fileoverview Boter Kaas en Eieren game logic hook
 * @description Manages game state, moves, win detection, and real-time sync
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Board,
  BoardPosition,
  CellValue,
  PlayerSymbol,
  GameStatus,
  TicTacToeGameState,
  WinLine,
  AIDifficulty,
  EMPTY_BOARD,
  WIN_COMBINATIONS,
} from "../types";

interface UseTicTacToeOptions {
  sessionId: string;
  mode: "solo" | "versus" | "practice";
  aiDifficulty?: AIDifficulty;
  onGameEnd?: (result: {
    winner_id: string | null;
    is_draw: boolean;
    move_count: number;
  }) => void;
}

interface UseTicTacToeReturn {
  board: Board;
  currentTurn: PlayerSymbol;
  mySymbol: PlayerSymbol | null;
  isMyTurn: boolean;
  gameStatus: GameStatus;
  winner: PlayerSymbol | null;
  winLine: WinLine | null;
  animatingCell: BoardPosition | null;
  opponentInfo: { id: string; name: string; avatar_url?: string | null } | null;
  moveCount: number;
  isLoading: boolean;
  error: string | null;
  makeMove: (position: BoardPosition) => Promise<boolean>;
  resetGame: () => Promise<void>;
  forfeit: () => Promise<void>;
}

/**
 * Check if there's a winner on the board
 */
const checkWinner = (board: Board): WinLine | null => {
  for (const [a, b, c] of WIN_COMBINATIONS) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return {
        positions: [a, b, c],
        winner: board[a] as PlayerSymbol,
      };
    }
  }
  return null;
};

/**
 * Check if board is full (draw)
 */
const isBoardFull = (board: Board): boolean => {
  return board.every((cell) => cell !== null);
};

/**
 * AI move calculation
 */
const calculateAIMove = (
  board: Board,
  aiSymbol: PlayerSymbol,
  difficulty: AIDifficulty
): BoardPosition | null => {
  const emptyPositions = board
    .map((cell, index) => (cell === null ? index : null))
    .filter((pos) => pos !== null) as BoardPosition[];

  if (emptyPositions.length === 0) return null;

  const playerSymbol: PlayerSymbol = aiSymbol === "X" ? "O" : "X";

  // Easy: Random move
  if (difficulty === "easy") {
    return emptyPositions[Math.floor(Math.random() * emptyPositions.length)];
  }

  // Medium: Sometimes smart, sometimes random
  if (difficulty === "medium" && Math.random() < 0.4) {
    return emptyPositions[Math.floor(Math.random() * emptyPositions.length)];
  }

  // Hard/Medium (when smart): Use minimax-lite strategy
  // 1. Check if AI can win
  for (const pos of emptyPositions) {
    const testBoard = [...board] as Board;
    testBoard[pos] = aiSymbol;
    if (checkWinner(testBoard)) return pos;
  }

  // 2. Block player from winning
  for (const pos of emptyPositions) {
    const testBoard = [...board] as Board;
    testBoard[pos] = playerSymbol;
    if (checkWinner(testBoard)) return pos;
  }

  // 3. Take center if available
  if (emptyPositions.includes(4)) return 4;

  // 4. Take a corner
  const corners: BoardPosition[] = [0, 2, 6, 8];
  const availableCorners = corners.filter((c) => emptyPositions.includes(c));
  if (availableCorners.length > 0) {
    return availableCorners[
      Math.floor(Math.random() * availableCorners.length)
    ];
  }

  // 5. Take any available position
  return emptyPositions[Math.floor(Math.random() * emptyPositions.length)];
};

/**
 * Main Boter Kaas en Eieren game hook
 * Uses activeProfile to support guardian mode
 */
export const useTicTacToe = ({
  sessionId,
  mode,
  aiDifficulty = "medium",
  onGameEnd,
}: UseTicTacToeOptions): UseTicTacToeReturn => {
  const { activeProfile } = useAuth();
  const targetUserId = activeProfile?.id;

  const [board, setBoard] = useState<Board>(EMPTY_BOARD);
  const [currentTurn, setCurrentTurn] = useState<PlayerSymbol>("X");
  const [mySymbol, setMySymbol] = useState<PlayerSymbol | null>(null);
  const [gameStatus, setGameStatus] = useState<GameStatus>("waiting");
  const [winner, setWinner] = useState<PlayerSymbol | null>(null);
  const [winLine, setWinLine] = useState<WinLine | null>(null);
  const [animatingCell, setAnimatingCell] = useState<BoardPosition | null>(
    null
  );
  const [moveCount, setMoveCount] = useState(0);
  const [opponentInfo, setOpponentInfo] = useState<{
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null>(null);
  const [gameState, setGameState] = useState<TicTacToeGameState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Ref to prevent multiple simultaneous loads (important for iOS)
  const isLoadingRef = useRef(false);

  const isMyTurn = useMemo(() => {
    return !!(mySymbol && gameStatus === "playing" && currentTurn === mySymbol);
  }, [mySymbol, currentTurn, gameStatus]);

  /**
   * Load initial game state from session
   */
  const loadGameState = useCallback(async () => {
    if (!sessionId || !targetUserId) {
      return;
    }
    
    // Prevent multiple simultaneous loads (important for iOS where realtime updates are faster)
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .select("*")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      const state = session.game_state as TicTacToeGameState | null;
      const participants = session.participants as Array<{
        user_id: string;
        role: string;
        symbol?: string;
        name?: string;
        avatar_url?: string;
      }> | null;

      if (state) {
        setBoard(state.board);
        setCurrentTurn(state.current_turn);
        setGameStatus(state.status);
        setWinner(state.winner);
        setWinLine(state.win_line);
        setMoveCount(state.move_count);
        setGameState(state);

        // Determine my symbol (use targetUserId for guardian mode)
        if (state.players.X === targetUserId) {
          setMySymbol("X");
        } else if (state.players.O === targetUserId) {
          setMySymbol("O");
        } else {
          console.error("❌ Could not determine symbol!", { players: state.players, targetUserId });
        }

        // Set opponent info from game state (use targetUserId for guardian mode)
        const opponentId =
          state.players.X === targetUserId ? state.players.O : state.players.X;
        if (opponentId !== "ai") {
          // Find opponent in participants
          const opponent = participants?.find((p) => p.user_id === opponentId);
          if (opponent) {
            setOpponentInfo({
              id: opponent.user_id,
              name: opponent.name || "Tegenstander",
              avatar_url: opponent.avatar_url || null,
            });
          }
        } else {
          setOpponentInfo({
            id: "ai",
            name: `AI (${aiDifficulty})`,
            avatar_url: null,
          });
        }
      } else {
        // Initialize new game state (use targetUserId for guardian mode)
        const isSolo = mode === "solo" || mode === "practice";
        const newState: TicTacToeGameState = {
          board: EMPTY_BOARD,
          current_turn: "X",
          status: "playing",
          winner: null,
          win_line: null,
          move_count: 0,
          last_move_at: null,
          players: {
            X: targetUserId,
            O: isSolo ? "ai" : (session.player2?.id ?? "waiting"),
          },
          move_history: [],
        };

        await supabase
          .from("game_sessions")
          .update({
            game_state: newState,
            status: "active",
          })
          .eq("id", sessionId);

        setBoard(newState.board);
        setCurrentTurn(newState.current_turn);
        setGameStatus(newState.status);
        setMySymbol("X");
        setGameState(newState);

        if (isSolo) {
          setOpponentInfo({
            id: "ai",
            name: `AI (${aiDifficulty})`,
            avatar_url: null,
          });
        }
      }
    } catch (err) {
      console.error("Error loading game state:", err);
      setError("Kon spel niet laden");
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [sessionId, targetUserId, mode, aiDifficulty]);

  /**
   * Subscribe to real-time updates
   */
  useEffect(() => {
    if (!sessionId || mode === "solo" || mode === "practice") return;

    const channel = supabase
      .channel(`tictactoe:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newState = payload.new.game_state as TicTacToeGameState;
          const prevStatus = gameStatus;
          
          if (newState) {
            setBoard(newState.board);
            setCurrentTurn(newState.current_turn);
            setGameStatus(newState.status);
            setWinner(newState.winner);
            setWinLine(newState.win_line);
            setMoveCount(newState.move_count);
            setGameState(newState);

            // Call onGameEnd for the opponent when game ends via realtime update
            // This ensures both players see the result/next game modal
            if (
              onGameEnd &&
              prevStatus === "playing" &&
              (newState.status === "finished" || newState.status === "draw")
            ) {
              const winnerId = newState.winner
                ? newState.players?.[newState.winner] ?? null
                : null;
              onGameEnd({
                winner_id: winnerId,
                is_draw: newState.status === "draw",
                move_count: newState.move_count,
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, mode, gameStatus, onGameEnd, loadGameState]);

  /**
   * Load game on mount and when session changes
   */
  useEffect(() => {
    loadGameState();
  }, [loadGameState]);


  /**
   * Make a move on the board
   */
  const makeMove = useCallback(
    async (position: BoardPosition): Promise<boolean> => {
      if (!targetUserId || !mySymbol || !isMyTurn || gameStatus !== "playing") {
        return false;
      }

      if (board[position] !== null) {
        return false;
      }

      // Animate the cell
      setAnimatingCell(position);

      // Create new board state
      const newBoard = [...board] as Board;
      newBoard[position] = mySymbol;

      // Check for winner
      const winResult = checkWinner(newBoard);
      const isDraw = !winResult && isBoardFull(newBoard);

      const newState: TicTacToeGameState = {
        ...gameState!,
        board: newBoard,
        current_turn: mySymbol === "X" ? "O" : "X",
        status: winResult ? "finished" : isDraw ? "draw" : "playing",
        winner: winResult?.winner ?? null,
        win_line: winResult ?? null,
        move_count: moveCount + 1,
        last_move_at: new Date().toISOString(),
        move_history: [
          ...(gameState?.move_history ?? []),
          {
            position,
            symbol: mySymbol,
            timestamp: new Date().toISOString(),
          },
        ],
      };

      // Update local state immediately for responsiveness
      setBoard(newBoard);
      setCurrentTurn(newState.current_turn);
      setMoveCount(newState.move_count);

      if (winResult) {
        setWinner(winResult.winner);
        setWinLine(winResult);
        setGameStatus("finished");
      } else if (isDraw) {
        setGameStatus("draw");
      }

      // Update database
      try {
        const updateData: Record<string, unknown> = {
          game_state: newState,
        };

        if (winResult || isDraw) {
          updateData.status = "completed";
          updateData.completed_at = new Date().toISOString();
          if (winResult) {
            updateData.winner_user_id =
              winResult.winner === mySymbol
                ? targetUserId
                : gameState?.players[winResult.winner] === "ai"
                ? null
                : gameState?.players[winResult.winner];
          }
        }

        await supabase
          .from("game_sessions")
          .update(updateData)
          .eq("id", sessionId);

        // Call onGameEnd callback (use targetUserId for guardian mode)
        if ((winResult || isDraw) && onGameEnd) {
          onGameEnd({
            winner_id: winResult
              ? winResult.winner === mySymbol
                ? targetUserId ?? null
                : gameState?.players[winResult.winner] ?? null
              : null,
            is_draw: isDraw,
            move_count: newState.move_count,
          });
        }

        // Clear animation after a short delay
        setTimeout(() => setAnimatingCell(null), 300);

        // Handle AI move if playing solo and game isn't over
        if (
          (mode === "solo" || mode === "practice") &&
          !winResult &&
          !isDraw
        ) {
          setTimeout(async () => {
            const aiSymbol = mySymbol === "X" ? "O" : "X";
            const aiMove = calculateAIMove(newBoard, aiSymbol, aiDifficulty);

            if (aiMove !== null) {
              setAnimatingCell(aiMove);

              const aiBoard = [...newBoard] as Board;
              aiBoard[aiMove] = aiSymbol;

              const aiWinResult = checkWinner(aiBoard);
              const aiIsDraw = !aiWinResult && isBoardFull(aiBoard);

              const aiState: TicTacToeGameState = {
                ...newState,
                board: aiBoard,
                current_turn: mySymbol,
                status: aiWinResult ? "finished" : aiIsDraw ? "draw" : "playing",
                winner: aiWinResult?.winner ?? null,
                win_line: aiWinResult ?? null,
                move_count: newState.move_count + 1,
                last_move_at: new Date().toISOString(),
                move_history: [
                  ...newState.move_history,
                  {
                    position: aiMove,
                    symbol: aiSymbol,
                    timestamp: new Date().toISOString(),
                  },
                ],
              };

              setBoard(aiBoard);
              setCurrentTurn(mySymbol);
              setMoveCount(aiState.move_count);
              setGameState(aiState);

              if (aiWinResult) {
                setWinner(aiWinResult.winner);
                setWinLine(aiWinResult);
                setGameStatus("finished");

                if (onGameEnd) {
                  onGameEnd({
                    winner_id: "ai",
                    is_draw: false,
                    move_count: aiState.move_count,
                  });
                }
              } else if (aiIsDraw) {
                setGameStatus("draw");
                if (onGameEnd) {
                  onGameEnd({
                    winner_id: null,
                    is_draw: true,
                    move_count: aiState.move_count,
                  });
                }
              }

              const aiUpdateData: Record<string, unknown> = {
                game_state: aiState,
              };

              if (aiWinResult || aiIsDraw) {
                aiUpdateData.status = "completed";
                aiUpdateData.completed_at = new Date().toISOString();
                // AI won, so no human winner (use targetUserId for guardian mode)
                if (aiWinResult && aiWinResult.winner !== mySymbol) {
                  aiUpdateData.winner_user_id = null; // AI won
                } else if (aiWinResult) {
                  aiUpdateData.winner_user_id = targetUserId;
                }
              }

              await supabase
                .from("game_sessions")
                .update(aiUpdateData)
                .eq("id", sessionId);

              setTimeout(() => setAnimatingCell(null), 300);
            }
          }, 600); // Delay AI move for better UX
        }

        return true;
      } catch (err) {
        console.error("Error making move:", err);
        setError("Kon zet niet opslaan");
        return false;
      }
    },
    [
      targetUserId,
      mySymbol,
      isMyTurn,
      gameStatus,
      board,
      gameState,
      moveCount,
      sessionId,
      mode,
      aiDifficulty,
      onGameEnd,
    ]
  );

  /**
   * Reset the game for a rematch
   */
  const resetGame = useCallback(async () => {
    if (!targetUserId || !gameState) return;

    const newState: TicTacToeGameState = {
      board: EMPTY_BOARD,
      current_turn: "X",
      status: "playing",
      winner: null,
      win_line: null,
      move_count: 0,
      last_move_at: null,
      players: gameState.players,
      move_history: [],
    };

    try {
      await supabase
        .from("game_sessions")
        .update({
          game_state: newState,
          status: "active",
          winner_user_id: null,
          completed_at: null,
        })
        .eq("id", sessionId);

      setBoard(EMPTY_BOARD);
      setCurrentTurn("X");
      setGameStatus("playing");
      setWinner(null);
      setWinLine(null);
      setMoveCount(0);
      setGameState(newState);
      setAnimatingCell(null);
    } catch (err) {
      console.error("Error resetting game:", err);
      setError("Kon spel niet resetten");
    }
  }, [targetUserId, gameState, sessionId]);

  /**
   * Forfeit the current game
   */
  const forfeit = useCallback(async () => {
    if (!targetUserId || !mySymbol || gameStatus !== "playing") return;

    const opponentSymbol = mySymbol === "X" ? "O" : "X";
    const opponentId = gameState?.players[opponentSymbol];

    try {
      await supabase
        .from("game_sessions")
        .update({
          status: "completed",
          winner_user_id: opponentId === "ai" ? null : opponentId,
          completed_at: new Date().toISOString(),
          game_state: {
            ...gameState,
            status: "finished",
            winner: opponentSymbol,
          },
        })
        .eq("id", sessionId);

      setGameStatus("finished");
      setWinner(opponentSymbol);

      if (onGameEnd) {
        onGameEnd({
          winner_id: opponentId ?? null,
          is_draw: false,
          move_count: moveCount,
        });
      }
    } catch (err) {
      console.error("Error forfeiting game:", err);
      setError("Kon spel niet opgeven");
    }
  }, [targetUserId, mySymbol, gameStatus, gameState, sessionId, onGameEnd, moveCount]);

  return {
    board,
    currentTurn,
    mySymbol,
    isMyTurn,
    gameStatus,
    winner,
    winLine,
    animatingCell,
    opponentInfo,
    moveCount,
    isLoading,
    error,
    makeMove,
    resetGame,
    forfeit,
  };
};

export default useTicTacToe;

